import { useCallback, useEffect } from 'react';
import { Pixie } from 'pixie';
import { stickers } from '../../../stickers';
import { base64ToFile } from '../../../utils/base64-to-file';
import { ImageSource } from './types';

interface InteractableRect {
    left: number;
    top: number;
    width: number;
    height: number;
    angle?: number;
}

let pi: Pixie;
let latestCallback: Function;

export const getPixie = (onSave: (data: string, filename: string, format: string) => void): Pixie => {
    latestCallback = onSave;

    if (pi) return pi;

    pi = new Pixie({
        crossOrigin: true,
        baseUrl: 'https://secretassets.xyz/pixie/assets',
        selector: '#pixie-container',
        tools: {
            stickers: {
                replaceDefault: false,
                items: stickers,
            },
        },
        ui: {
            activeTheme: 'dark',
            allowEditorClose: true,
            openImageDialog: {
                show: false,
            },
        },
        onSave: (data: string, filename: string, format: string) => {
            latestCallback(data, filename, format);
        },
    });

    return pi;
};

export function cropCenter({ width, height }: Pick<InteractableRect, 'height' | 'width'>): InteractableRect {
    let diff = Math.abs(width - height);

    if (height > width) {
        return { left: 0, top: diff / 2, width, height: height - diff };
    } else {
        return { left: diff / 2, top: 0, width: width - diff, height };
    }
}

export function ImageEditor({
    imageSrc,
    children,
    onImageChange,
}: ImageSource & {
    children: (crop: (cropFn: (box: Pick<InteractableRect, 'height' | 'width'>) => InteractableRect) => void, open: () => void) => JSX.Element;
}) {
    const pixieInstance = getPixie((data: string, filename: string, format: string) => {
        onImageChange(data, base64ToFile(data, `${filename}.${format}`));

        const pixieContainer = document.getElementById('pixie-container');
        pixieContainer!.style.opacity = '0';
        pixieContainer!.style.pointerEvents = 'none';
    });

    useEffect(() => {
        pixieInstance?.tools?.import?.openBackgroundImage(imageSrc);
    }, [pixieInstance, imageSrc]);

    const pixieCrop = pixieInstance?.tools.crop;
    const pixieExport = pixieInstance?.tools.export;

    const crop = useCallback(
        (cropFn: (box: Pick<InteractableRect, 'height' | 'width'>) => InteractableRect) => {
            if (imageSrc) {
                var image = new Image();
                image.src = imageSrc;
                image.onload = function () {
                    let { width, height } = this as any;
                    pixieCrop.apply(cropFn({ width, height }));
                    setTimeout(() => {
                        let image = pixieExport.getDataUrl();
                        if (image) {
                            onImageChange(image, base64ToFile(image, 'profilePic.jpg'));
                        }
                    }, 200);
                };
            }
        },
        [imageSrc, onImageChange, pixieCrop, pixieExport],
    );

    const open = useCallback(() => {
        if (imageSrc) {
            pixieInstance?.open({ image: imageSrc });
            const pixieContainer = document.getElementById('pixie-container');
            pixieContainer!.style.opacity = '1';
            pixieContainer!.style.pointerEvents = 'all';
        }
    }, [imageSrc, pixieInstance]);

    return children(crop, open);
}
